<template>
    <div v-if="isSendable" class="message-input-section">
        <!-- Hidden File Input -->
        <input 
            ref="fileInput" 
            type="file" 
            @change="handleFileUpload" 
            style="display: none" 
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
        />

        <!-- Emoji Picker -->
        <div v-if="showEmojiPicker" class="emoji-picker" @click.stop>
            <div class="emoji-picker-header">
                <span>Choose an emoji</span>
                <button @click="closeEmojiPicker" class="close-emoji-btn">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="emoji-grid">
                <button 
                    v-for="emoji in commonEmojis" 
                    :key="emoji" 
                    @click="insertEmoji(emoji)"
                    class="emoji-btn"
                >
                    {{ emoji }}
                </button>
            </div>
        </div>

        <!-- Autotext Suggestions -->
        <div v-if="autotext.length > 0" class="autotext-suggestions" :style="{'bottom': bottomCSS + 'px'}">
            <div class="suggestions-header">
                <i class="fas fa-lightbulb"></i>
                <span>Quick Replies</span>
            </div>
            <div class="suggestions-list">
                <div v-for="(value, index) in autotext" :key="index" class="autotext-item" @click="selectAutotext(value)">
                    <span>{{ value }}</span>
                </div>
            </div>
        </div>

        <!-- Input Container -->
        <div class="message-input-container">
            <!-- Left Actions -->
            <div class="input-actions">
                <button class="action-btn attach-btn" title="Attach file" @click="attachFile">
                    <i class="fas fa-paperclip"></i>
                </button>
                <button class="action-btn emoji-btn" title="Add emoji" @click="toggleEmojiPicker" :class="{ active: showEmojiPicker }">
                    <i class="fas fa-smile"></i>
                </button>
            </div>

            <!-- Text Input -->
            <div class="text-input-area">
                <textarea
                    v-model="message"
                    @keyup.enter="sendMessage"
                    @input="$emit('update:message', $event.target.value)"
                    placeholder="Type your message..."
                    class="message-textarea"
                    rows="1"
                    ref="textarea"
                ></textarea>
            </div>

            <!-- Send Button -->
            <button
                @click="sendMessage"
                class="send-button"
                :disabled="!message.trim()"
                title="Send message"
            >
                <i class="fas fa-paper-plane"></i>
            </button>
        </div>

        <!-- Input Status -->
        <div class="input-status" v-if="message.trim() || isTyping">
            <div class="status-left">
                <span class="char-count" :class="charCountClass">{{ charCount }}/1000</span>
            </div>
            <div class="status-right">
                <span class="typing-indicator" v-if="isTyping">
                    <div class="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <span class="typing-text">Customer is typing...</span>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MessageInput',
    props: {
        isSendable: {
            type: Boolean,
            default: false
        },
        message: {
            type: String,
            default: ''
        },
        autotext: {
            type: Array,
            default: () => []
        },
        bottomCSS: {
            type: String,
            default: '105px'
        },
        isTyping: {
            type: Boolean,
            default: false
        },
        charCount: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            showEmojiPicker: false,
            commonEmojis: [
                '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
                '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
                '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
                '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
                '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
                '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠',
                '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨',
                '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥',
                '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
                '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
                '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑',
                '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻',
                '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸',
                '😹', '😻', '😼', '😽', '🙀', '😿', '😾'
            ]
        };
    },
    computed: {
        charCountClass() {
            if (this.charCount > 900) return 'danger';
            if (this.charCount > 800) return 'warning';
            return '';
        }
    },
    watch: {
        message() {
            this.$nextTick(() => {
                this.autoResize();
            });
        }
    },
    methods: {
        selectAutotext(value) {
            this.$emit('update:message', value);
        },
        attachFile() {
            this.$refs.fileInput.click();
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.$emit('update-file', file);
                // Reset the input so the same file can be selected again
                event.target.value = '';
            }
        },
        toggleEmojiPicker() {
            this.showEmojiPicker = !this.showEmojiPicker;
        },
        closeEmojiPicker() {
            this.showEmojiPicker = false;
        },
        insertEmoji(emoji) {
            const textarea = this.$refs.textarea;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = this.message;
            
            this.message = text.substring(0, start) + emoji + text.substring(end);
            this.$emit('update:message', this.message);
            
            // Focus back to textarea and position cursor after emoji
            this.$nextTick(() => {
                textarea.focus();
                textarea.setSelectionRange(start + emoji.length, start + emoji.length);
            });
            
            this.closeEmojiPicker();
        },
        updateFile(file) {
            this.$emit('update-file', file);
        },
        sendMessage() {
            this.$emit('send-message');
        },
        autoResize() {
            const textarea = this.$refs.textarea;
            if (textarea) {
                textarea.style.height = 'auto';
                textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
            }
        }
    },
    mounted() {
        this.autoResize();
        // Close emoji picker when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.$el.contains(e.target)) {
                this.showEmojiPicker = false;
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeEmojiPicker);
    }
};
</script>

<style scoped>
/* Message Input Section */
.message-input-section {
    background: white;
    border-top: 1px solid #e0e0e0;
    padding: 16px 20px;
    position: relative;
    flex-shrink: 0;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    margin-top: auto;
}

.autotext-suggestions {
    position: absolute;
    bottom: 100%;
    left: 20px;
    right: 20px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-height: 200px;
    overflow: hidden;
    z-index: 1000;
    margin-bottom: 8px;
}

.suggestions-header {
    background: #f5f5f5;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #424242;
    letter-spacing: 0.25px;
}

.suggestions-header i {
    color: #1976d2;
    font-size: 1rem;
}

.suggestions-list {
    max-height: 150px;
    overflow-y: auto;
}

.autotext-item {
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border-bottom: 1px solid #f5f5f5;
}

.autotext-item:last-child {
    border-bottom: none;
}

.autotext-item:hover {
    background: #e3f2fd;
}

.autotext-item span {
    flex: 1;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #212121;
    font-weight: 400;
    letter-spacing: 0.25px;
}

/* Message Input Container */
.message-input-container {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: #f5f5f5;
    border-radius: 24px;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 48px;
}

.message-input-container:focus-within {
    background: white;
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.input-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    align-items: center;
}

.action-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: transparent;
    border: none;
    color: #757575;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 1.125rem;
}

.action-btn:hover {
    background: #e0e0e0;
    color: #1976d2;
}

.action-btn:active {
    transform: scale(0.95);
}

.action-btn.active {
    background: #e3f2fd;
    color: #1976d2;
}

.text-input-area {
    flex: 1;
    min-height: 32px;
    max-height: 120px;
    overflow-y: auto;
    display: flex;
    align-items: center;
}

.message-textarea {
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #212121;
    background: transparent;
    min-height: 20px;
    max-height: 120px;
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    letter-spacing: 0.25px;
    overflow-y: auto;
    padding: 6px 0;
}

.message-textarea::placeholder {
    color: #9e9e9e;
}

.send-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #1976d2;
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.875rem;
    flex-shrink: 0;
}

.send-button:hover:not(:disabled) {
    background: #1565c0;
    transform: scale(1.05);
}

.send-button:active:not(:disabled) {
    transform: scale(0.95);
}

.send-button:disabled {
    background: #e0e0e0;
    color: #9e9e9e;
    cursor: not-allowed;
    transform: none;
}

.input-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    color: #757575;
    padding: 8px 4px 0 4px;
}

.status-left {
    display: flex;
    align-items: center;
}

.status-right {
    display: flex;
    align-items: center;
}

.char-count {
    font-weight: 500;
    letter-spacing: 0.25px;
}

.char-count.warning {
    color: #ff9800;
}

.char-count.danger {
    color: #f44336;
}

.typing-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4caf50;
    font-style: italic;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.typing-dots {
    display: flex;
    gap: 2px;
}

.typing-dots span {
    width: 4px;
    height: 4px;
    background: #4caf50;
    border-radius: 50%;
    animation: typingDot 1.4s infinite;
}

.typing-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

.typing-text {
    font-size: 0.75rem;
}

@keyframes typingDot {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-10px); }
}

/* Emoji Picker */
.emoji-picker {
    position: absolute;
    bottom: 100%;
    left: 20px;
    right: 20px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-height: 200px;
    overflow: hidden;
    z-index: 1000;
    margin-bottom: 8px;
}

.emoji-picker-header {
    background: #f5f5f5;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: #424242;
    letter-spacing: 0.25px;
}

.close-emoji-btn {
    background: none;
    border: none;
    color: #757575;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-emoji-btn:hover {
    background: #e0e0e0;
    color: #212121;
}

.emoji-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 4px;
    padding: 12px;
    max-height: 150px;
    overflow-y: auto;
}

.emoji-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
}

.emoji-btn:hover {
    background: #f0f0f0;
    transform: scale(1.1);
}

.emoji-btn:active {
    transform: scale(0.95);
}

/* Responsive Design */
@media (max-width: 768px) {
    .message-input-section {
        padding: 12px 16px;
    }

    .message-input-container {
        padding: 6px 10px;
        min-height: 44px;
    }

    .action-btn {
        width: 32px;
        height: 32px;
        font-size: 1rem;
    }

    .send-button {
        width: 32px;
        height: 32px;
        font-size: 0.75rem;
    }
}
</style>
